'use client'
import {
  Button,
  Card,
  Checkbox,
  colorsTuple,
  Combobox,
  createTheme,
  Divider,
  Drawer,
  Input,
  Loader,
  Menu,
  Modal,
  Notification,
  Pagination,
  Paper,
  Radio,
  ScrollArea,
  Select,
  Skeleton,
  Switch,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core'

import buttonClasses from './componentCSSModules/Button.module.css'
import cardClasses from './componentCSSModules/Card.module.css'
import checkboxClasses from './componentCSSModules/Checkbox.module.css'
import comboboxClasses from './componentCSSModules/Combobox.module.css'
import drawerClasses from './componentCSSModules/Drawer.module.css'
import loaderClasses from './componentCSSModules/Loader.module.css'
import menuClasses from './componentCSSModules/Menu.module.css'
import modalClasses from './componentCSSModules/Modal.module.css'
import notificationClasses from './componentCSSModules/Notification.module.css'
import paginationClasses from './componentCSSModules/Pagination.module.css'
import radioClasses from './componentCSSModules/Radio.module.css'
import scrollareaClasses from './componentCSSModules/ScrollArea.module.css'
import selectClasses from './componentCSSModules/Select.module.css'
import skeletonClasses from './componentCSSModules/Skeleton.module.css'
import switchClasses from './componentCSSModules/Switch.module.css'
import typographyClasses from './componentCSSModules/Text.module.css'
import textInputClasses from './componentCSSModules/TextInput.module.css'
import tooltipClasses from './componentCSSModules/Tooltip.module.css'
import palette from './palette'

const { blue, ...otherColors } = palette

// https://mantine.dev/theming/theme-object/
export const theme = createTheme({
  primaryColor: 'primary',

  primaryShade: 4,

  colors: {
    primary: colorsTuple(Object.values(blue)),
    ...Object.fromEntries(
      Object.entries(otherColors).map(([k, v]) => [k, colorsTuple(Object.values(v))]),
    ),
  },

  // Breakpoints for built-in Mantine component, for media query break points in css module,
  // they will be defined in postcss file, see https://mantine.dev/styles/responsive/#breakpoints-variables-in-css-modules
  // Ref: https://www.figma.com/design/IhN9oKdUP47Zk75t71JSZu/Crypto.com-WebApp?node-id=16517-200802&node-type=SECTION&m=dev
  breakpoints: {
    small: '0px',
    medium: '768px',
    midScale: '992px',
    large: '1200px',
    // Max width of the main content area
    contentLimit: '1312px',
    xlarge: '1440px',
  },

  // Spacing Spec: https://www.figma.com/design/sBsInZjr4mVXk7QHtlSjf5/MA-%7C-Library-v1.2?node-id=4455-47527&m=dev
  spacing: {
    '5xs': '1px',
    '4xs': '2px',
    '3xs': '4px',
    '2xs': '8px',
    xs: '12px',
    sm: '16px',
    md: '24px',
    lg: '32px',
    xl: '40px',
    '2xl': '48px',
    '3xl': '56px',
    '4xl': '64px',
    '5xl': '80px',
  },

  // Corner Radius Spec: https://www.figma.com/design/sBsInZjr4mVXk7QHtlSjf5/MA-%7C-Library-v1.2?node-id=28163-61420&m=dev
  radius: {
    '2xs': '2px',
    xs: '4px',
    sm: '8px',
    md: '10px',
    lg: '16px',
    xl: '16px',
    circle: '99px',
  },

  defaultRadius: 'md',

  cursorType: 'pointer',

  // Note: If you're adding a new component here, remember to also add the component to Storybook c.f https://mcoproduct.atlassian.net/wiki/spaces/MAW/pages/2892628121/Development+Guidelines#How-to-Add-a-New-Story
  components: {
    Button: Button.extend({
      classNames: buttonClasses,
    }),
    Card: Card.extend({
      classNames: cardClasses,
    }),
    Checkbox: Checkbox.extend({
      classNames: checkboxClasses,
    }),
    Divider: Divider.extend({
      vars: () => {
        return {
          root: {
            // 原引用 --border-surface 不存在（colorTokens.border 只有
            // surfaceDefault / surfaceEmphasis 等），var() 解析失败导致
            // Divider border-top 不绘制——侧栏分组线不可见（WWW-5075）。
            '--divider-color': 'var(--border-surface-default)',
          },
        }
      },
    }),
    Drawer: Drawer.extend({
      classNames: drawerClasses,
    }),
    InputWrapper: Input.Wrapper.extend({
      classNames: textInputClasses,
    }),
    Loader: Loader.extend({
      classNames: loaderClasses,
    }),
    Menu: Menu.extend({
      classNames: menuClasses,
    }),
    Modal: Modal.extend({
      classNames: modalClasses,
    }),
    Notification: Notification.extend({
      classNames: notificationClasses,
    }),
    NumberInput: TextInput.extend({
      classNames: textInputClasses,
      vars: () => {
        return {
          input: {
            '--input-bg': 'var(--bg-surface-1)',
          },
        }
      },
    }),
    Pagination: Pagination.extend({
      classNames: paginationClasses,
    }),
    Paper: Paper.extend({
      defaultProps: {
        radius: 'md',
      },
    }),
    Radio: Radio.extend({
      classNames: radioClasses,
      defaultProps: {
        color: 'var(--border-status-primary)',
        variant: 'outline',
      },
    }),
    ScrollArea: ScrollArea.extend({
      classNames: scrollareaClasses,
    }),
    Select: Select.extend({
      classNames: selectClasses,
    }),
    Skeleton: Skeleton.extend({
      classNames: skeletonClasses,
    }),
    Text: Text.extend({
      classNames: typographyClasses,
    }),
    TextInput: TextInput.extend({
      classNames: textInputClasses,
      vars: () => {
        return {
          input: {
            '--input-bg': 'var(--bg-surface-1)',
          },
        }
      },
    }),
    Tooltip: Tooltip.extend({
      classNames: tooltipClasses,
    }),
    Combobox: Combobox.extend({
      classNames: comboboxClasses,
    }),
    Switch: Switch.extend({
      classNames: switchClasses,
    }),
  },
})
